export type ButtonSizeOption = 'normal' | 'large' | 'larger';
export type PositioningOption = 'neutral' | 'centered';

export const EMBED_DEFAULT_LABEL = 'Book Now';
export const EMBED_DEFAULT_BUTTON_COLOR = '#5777da';
export const EMBED_DEFAULT_TEXT_COLOR = '#ffffff';
export const EMBED_DEFAULT_BUTTON_SIZE: ButtonSizeOption = 'normal';
export const EMBED_DEFAULT_POSITIONING: PositioningOption = 'centered';

export const VALID_BUTTON_SIZES: ButtonSizeOption[] = [
  'normal',
  'large',
  'larger',
];
export const VALID_POSITIONING: PositioningOption[] = ['neutral', 'centered'];

export interface BuildEmbedSnippetOptions {
  label: string;
  buttonColor: string;
  textColor: string;
  buttonSize: ButtonSizeOption;
  positioning: PositioningOption;
}

/**
 * Structured fields for consumers that render HTML locally (e.g. WordPress plugin).
 * Values match {@link buildEmbedSnippet} output deterministically.
 */
export interface BookingEmbedParts {
  script_src: string;
  booking_href: string;
  label: string;
  button_color: string;
  text_color: string;
  button_size: ButtonSizeOption;
  positioning: PositioningOption;
}

export interface BookingEmbedPayload {
  embed_code: string;
  embed_parts: BookingEmbedParts;
}

function normalizeWebsiteBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

export function appendUtmToBookingUrl(url: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}utm_source=inline-button-embed&utm_medium=embed`;
}

/**
 * Single source of truth for embed HTML string and structured parts.
 */
export function buildBookingEmbedPayload(
  baseUrl: string,
  bookingPageUrl: string,
  options: BuildEmbedSnippetOptions,
): BookingEmbedPayload {
  const normalizedBase = normalizeWebsiteBaseUrl(baseUrl);
  const booking_href = appendUtmToBookingUrl(bookingPageUrl);
  const script_src = `${normalizedBase}/embed/inline-button.js`;
  const embed_parts: BookingEmbedParts = {
    script_src,
    booking_href,
    label: options.label,
    button_color: options.buttonColor,
    text_color: options.textColor,
    button_size: options.buttonSize,
    positioning: options.positioning,
  };
  const embed_code = formatEmbedHtml(script_src, booking_href, options);
  return { embed_code, embed_parts };
}

function formatEmbedHtml(
  script_src: string,
  booking_href: string,
  options: BuildEmbedSnippetOptions,
): string {
  const escapedLabelAttribute = options.label.replace(/"/g, '&quot;');
  const escapedLabelHtml = options.label.replace(/</g, '&lt;');
  const linkAttrs = [
    `href="${booking_href}"`,
    'target="_blank"',
    'rel="noopener noreferrer"',
    'data-bwm',
    `data-bwm-label="${escapedLabelAttribute}"`,
    `data-bwm-color="${options.buttonColor}"`,
    `data-bwm-text-color="${options.textColor}"`,
    `data-bwm-size="${options.buttonSize}"`,
    `data-bwm-position="${options.positioning}"`,
  ].join(' ');

  return `<script async src="${script_src}"></script>
<a ${linkAttrs}>
  ${escapedLabelHtml}
</a>`;
}

export function buildEmbedSnippet(
  baseUrl: string,
  bookingPageUrl: string,
  options: BuildEmbedSnippetOptions,
): string {
  return buildBookingEmbedPayload(baseUrl, bookingPageUrl, options).embed_code;
}
